// Pure helpers for where a connection stands, on one Boo (its Connections
// home) and across the workspace (the org's Connections page). No React and no api
// client at runtime, so `node --test` covers it.

//   connected: in use (by this Boo, or by some Boo for the workspace).
//   elsewhere: the workspace has it, but another Boo holds it (a shared chat
//              app answers through one Boo at a time).
//   available: the workspace has it and no Boo holds it yet.
//   off:       not connected.
export type ConnectionState = "connected" | "elsewhere" | "available" | "off";

export interface ConnectionStatus {
  loading: boolean;
  state: ConnectionState;
  label: string;
}

// The shape every shared chat app status (Slack, Teams, Google Chat, Discord)
// has in common.
export interface SharedChatInstall {
  installed: boolean;
  // Connected to the org, attached or not. Older backends omit it.
  workspaceConnected?: boolean;
  containerId: string | null;
  containerName: string | null;
}

const NOT_CONNECTED = (loading: boolean): ConnectionStatus => ({ loading, state: "off", label: "Not connected" });

// Where a shared chat app stands for one Boo:
//   here:       this Boo answers through it.
//   elsewhere:  another Boo does.
//   unattached: the workspace has it and no Boo answers yet.
//   none:       the workspace doesn't have it.
export type BooAppState = "here" | "elsewhere" | "unattached" | "none";

export function booAppState(install: SharedChatInstall | undefined, containerId: string): BooAppState {
  if (!install) return "none";
  if (install.installed && install.containerId === containerId) return "here";
  if (install.installed && install.containerId) return "elsewhere";
  if (install.installed || install.workspaceConnected) return "unattached";
  return "none";
}

// A shared chat app as one Boo sees it. `place` is the workspace, tenant,
// server, or customer name.
export function booChatStatus(
  install: SharedChatInstall | undefined,
  loading: boolean,
  place: string | null | undefined,
  containerId: string,
): ConnectionStatus {
  const where = place ?? "your workspace";
  switch (booAppState(install, containerId)) {
    case "here":
      return { loading: false, state: "connected", label: `Connected to ${where}` };
    case "elsewhere":
      return { loading: false, state: "elsewhere", label: `${where} is with ${install?.containerName ?? "another Boo"}` };
    case "unattached":
      return { loading: false, state: "available", label: `${where} is ready to use` };
    default:
      return NOT_CONNECTED(!install && loading);
  }
}

// A shared chat app as the workspace sees it.
export function workspaceChatStatus(
  install: SharedChatInstall | undefined,
  loading: boolean,
  place: string | null | undefined,
): ConnectionStatus {
  if (!install) return NOT_CONNECTED(loading);
  const where = place ?? "your workspace";
  if (install.installed && install.containerId)
    return { loading: false, state: "connected", label: `${where}, used by ${install.containerName ?? "a Boo"}` };
  if (install.installed || install.workspaceConnected)
    return { loading: false, state: "available", label: `${where}, not used by a Boo yet` };
  return NOT_CONNECTED(false);
}

// One host's code host connections on a Boo: their namespaces, and whether one
// is the default for new apps.
export function booCodeHostStatus(
  active: ReadonlyArray<{ id: string; namespace: string }>,
  defaultConnectionId: string | null | undefined,
  loading: boolean,
): ConnectionStatus {
  if (active.length === 0) return NOT_CONNECTED(loading);
  const isDefault = active.some((c) => c.id === defaultConnectionId);
  return {
    loading: false,
    state: "connected",
    label: `${active.map((c) => c.namespace).join(", ")}${isDefault ? " · default for new apps" : ""}`,
  };
}

// One host's workspace connections: their namespaces, and how many Boos use
// any of them.
export function workspaceCodeHostStatus(
  pool: ReadonlyArray<{ namespace: string; booIds?: string[] }>,
  loading: boolean,
): ConnectionStatus {
  if (pool.length === 0) return NOT_CONNECTED(loading);
  const boos = new Set(pool.flatMap((c) => c.booIds ?? []));
  return {
    loading: false,
    state: "connected",
    label: `${pool.map((c) => c.namespace).join(", ")} · ${boos.size === 1 ? "1 Boo" : `${boos.size} Boos`}`,
  };
}
